import { useEffect, useRef, useState } from 'preact/hooks'
import { AlertTriangle, Calendar, Check, Film, Info, Loader2, Play, RotateCcw, Upload } from 'lucide-preact'
import { websApi, type WebViewDto } from '@/api/webs'
import { webinarRoomApi } from '@/api/webinarRoom'
import { CloudFilePicker } from '@/components/common/upload/CloudFilePicker'
import type { CloudProvider } from '@/components/common/upload/cloudFile'
import { useToast } from '@/hooks/useToast'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'
import { retryOnStaleETag } from '@/utils/etagRetry'
import { RECORDING_FIELD_NAME, endFittingRecording, formatRecordingLength, formatWallClockTime, qaAfterVideoLine, sessionEnd, sessionLengthSeconds } from '@/utils/webinarRoomSetup'
import { RoomRecordingPreview } from './RoomRecordingPreview'
import { RoomSubtitlesSection } from './RoomSubtitlesSection'
import { DropZone, Progress, ReplacementStrip, SourceIcon, type ReplacementCopy } from './RoomUploadParts'
import { STALLED_NOTE, waitingText } from './roomUpload'
import { importRequestOf, useRoomVideoUpload } from './useRoomVideoUpload'
import {
    LOCK_TITLE, PREPARING_TOO_LONG, ROOM_RECORDING_MAX_BYTES, cloudSourceOf, dropZoneLimits, failedFacts, leaveNote, preparingTooLong,
    recordingFacts, recordingSubLine, recordingView, replacementBusy, replacementView, transferText, uploadReplaces,
    uploadedByLine,
} from './roomRecording'
import { BUTTON, BUTTON_GHOST, CARD, CARD_SUB, CARD_TITLE, NOTE, PILL_READY, PILL_WARNING, STRIP_ERROR, STRIP_WARNING } from './roomStyles'

interface RoomRecordingCardProps {
    web: WebViewDto
    isSignedOff: boolean
    /** The session has run: the copy turns to what the viewer link plays now. */
    ended: boolean
    /** Rereads the web, after every write here and whenever the server says the recording moved on. */
    reload: () => Promise<void>
    onPatchWeb: (patcher: (prev: WebViewDto) => WebViewDto) => void
}

const uploadKey = (webId: string) => `webinar-room-recording:${webId}`

const REPLACEMENT_COPY: ReplacementCopy = {
    failedHeading: fileName => `Couldn’t prepare ${fileName} for streaming.`,
    currentKept: 'The current recording is still in use.',
    preparing: 'Uploaded. Preparing it for streaming, usually a few minutes. The current recording stays in use until then.',
    stuck: 'The current recording stays in use.',
    uploading: 'The current recording stays in use until this one is ready. Polls stay as they are.',
    keepCurrent: 'Keep the current recording',
}

/**
 * The Room tab's Recording card (ORB-1086/1087): upload from the computer straight to storage or import from the
 * cloud, preparing, failed, ready, too long, and a replacement on its way. The pill and the strips carry the
 * server's `OnlineEvent.Recording` sign-off item word for word, except that a failure says the recording's own
 * `FailureMessage` when the pipeline left one.
 */
export function RoomRecordingCard({ web, isSignedOff, ended, reload, onPatchWeb }: RoomRecordingCardProps) {
    const { canEdit } = useWorkspaceCapabilities()
    const { showErrorUnless422, showSuccess } = useToast()
    const event = web.OnlineEvent!
    const recording = event.Recording ?? null
    const pending = event.PendingRecording ?? null
    const polls = event.Polls ?? []
    const sessionSeconds = sessionLengthSeconds(event.StartDateTime, event.EndDateTime)
    const item = web.OutstandingForSignOff?.find(error => error.FieldName === RECORDING_FIELD_NAME) ?? null

    const replacing = uploadReplaces(recording)
    const [dropZoneOpen, setDropZoneOpen] = useState(false)
    const [cloudProvider, setCloudProvider] = useState<CloudProvider | null>(null)
    const [previewOpen, setPreviewOpen] = useState(false)
    const [previewWithSubtitles, setPreviewWithSubtitles] = useState(false)

    const workspaceId = web.WorkspaceId
    const webId = web.Id

    const closeChoosers = () => {
        setDropZoneOpen(false)
        setCloudProvider(null)
        setFileError(null)
    }

    const forgetPending = async () => {
        const res = await webinarRoomApi.cancelPendingRecording(workspaceId, webId)
        if (res.error) showErrorUnless422('Not cancelled', res, 'The recording on its way could not be cancelled.')
    }

    const { upload, busy, setBusy, fileError, setFileError, refetchWeb, uploadFromComputer, importFromCloud, cancelUpload, retry } = useRoomVideoUpload({
        web,
        uploadKey: uploadKey(webId),
        what: 'recording',
        reload,
        onPatchWeb,
        beginUpload: (document, file) => webinarRoomApi.beginRecordingUpload(workspaceId, webId, {
            ETag: document.ETag, FileName: file.name, SizeBytes: file.size, ContentType: file.contentType, Replace: replacing,
        }),
        beginImport: (document, file) => webinarRoomApi.importRecording(workspaceId, webId, { ETag: document.ETag, ...importRequestOf(file), Replace: replacing }),
        // The API drops whichever recording is on its way, so the id is not needed.
        abandon: forgetPending,
        onStarted: closeChoosers,
    })
    const cancelOnItsWay = () => cancelUpload(pending?.Id ?? recording?.Id ?? null)
    const uploadRunning = upload?.status === 'uploading' || upload?.status === 'finishing'
    const view = recordingView(recording, sessionSeconds, uploadRunning && !replacing)
    const replacement = replacementView(pending, uploadRunning && replacing)
    const replacementOnItsWay = replacementBusy(replacement)
    const now = new Date()
    const stuck = view === 'preparing' && preparingTooLong(recording, now)

    const locked = isSignedOff || !canEdit
    const lockTitle = isSignedOff ? LOCK_TITLE : undefined

    // The server swaps a ready replacement in by itself, while the web is open for changes.
    const lastPending = useRef<{ id: string; fileName: string } | null>(null)
    useEffect(() => {
        const was = lastPending.current
        if (was && recording?.Id === was.id) showSuccess('Recording replaced', `${was.fileName} is the recording now.`)
        lastPending.current = pending ? { id: pending.Id, fileName: pending.FileName } : null
    }, [recording?.Id, pending?.Id])

    const fitSessionToRecording = async () => {
        setBusy(true)
        const res = await retryOnStaleETag(document => {
            const duration = document.OnlineEvent?.Recording?.DurationSeconds
            const end = duration != null ? endFittingRecording(document.OnlineEvent?.StartDateTime, duration) : null
            if (!end) throw new Error('A too-long recording always has a start and a length to fit the session to')
            return websApi.updateOnlineEventDetails(document.WorkspaceId, document.Id, { ETag: document.ETag, EndDateTime: end })
        }, web, refetchWeb).finally(() => setBusy(false))
        if (res.data) {
            const saved = res.data
            onPatchWeb(prev => ({ ...prev, ...saved }))
            const end = sessionEnd(saved.OnlineEvent?.EndDateTime)
            showSuccess('Event details updated', end ? `The session now ends at ${formatWallClockTime(end)}.` : 'Your changes have been saved.')
        } else {
            showErrorUnless422('Save failed', res, 'Unable to update event details')
        }
    }

    const openReplace = () => { setPreviewOpen(false); setDropZoneOpen(true) }
    const openPreview = (withSubtitles: boolean) => { setPreviewWithSubtitles(withSubtitles); setPreviewOpen(true) }

    const pill = item
        ? <span className={PILL_WARNING} title={item.Message}><AlertTriangle className="w-3 h-3" />Needed before sign-off</span>
        : recording?.State === 'Ready' ? <span className={PILL_READY}><Check className="w-3 h-3" />Ready</span> : null

    const chooser = (replace: boolean) => (
        <>
            <DropZone what={replace ? 'new recording' : 'recording'} limits={dropZoneLimits(sessionSeconds)} disabled={busy} onFile={file => void uploadFromComputer(file)} onCloud={provider => { setFileError(null); setCloudProvider(provider) }} />
            {fileError && <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">{fileError}</p>}
            {cloudProvider && (
                <div className="mt-4 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                    <CloudFilePicker enabled provider={cloudProvider} onProviderChange={provider => setCloudProvider(provider)} maxBytes={ROOM_RECORDING_MAX_BYTES} onPicked={file => void importFromCloud(file)} />
                    <div className="mt-4 flex items-center justify-end gap-3">
                        {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" aria-label="Starting the import" />}
                        <button type="button" className={BUTTON_GHOST} onClick={() => setCloudProvider(null)}>Cancel</button>
                    </div>
                </div>
            )}
        </>
    )

    const replaceTitle = isSignedOff ? LOCK_TITLE : replacementOnItsWay ? 'A replacement is already on its way' : undefined
    const replaceDisabled = locked || busy || dropZoneOpen || replacementOnItsWay

    const body = (() => {
        if (view === 'empty' || ((view === 'failed' || stuck) && dropZoneOpen)) {
            return canEdit && !isSignedOff
                ? chooser(false)
                : <p className="text-sm text-slate-500 dark:text-slate-400">No recording uploaded yet.</p>
        }
        if (view === 'uploading') {
            const source = cloudSourceOf(recording)
            const local = upload && !replacing ? upload : null
            return (
                <>
                    <div className="grid grid-cols-[44px_minmax(0,1fr)_auto] gap-3.5 items-center">
                        <span className="w-11 h-11 rounded-lg grid place-items-center bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 text-slate-400">
                            <SourceIcon source={local ? null : source} className="w-5 h-5" />
                        </span>
                        <div className="min-w-0">
                            <p className="font-medium text-slate-900 dark:text-white truncate">{local?.fileName ?? recording?.FileName}</p>
                            <Progress fraction={local && local.totalBytes > 0 ? local.uploadedBytes / local.totalBytes : null} />
                            <p className="mt-1.5 text-sm tabular-nums text-slate-500 dark:text-slate-400">
                                {local ? transferText(local.uploadedBytes, local.totalBytes, local.secondsLeft, true) : waitingText(recording, source)}
                            </p>
                        </div>
                        {canEdit && <button type="button" className={BUTTON} disabled={busy} onClick={() => void cancelOnItsWay()}>Cancel</button>}
                    </div>
                    <p className={NOTE}>
                        <Info className="w-3.5 h-3.5 mt-0.5 flex-none text-slate-400" />
                        <span>{local || source ? leaveNote(local ? null : source) : STALLED_NOTE}</span>
                    </p>
                </>
            )
        }
        if (view === 'preparing' && !stuck) {
            return (
                <>
                    <div className="grid grid-cols-[44px_minmax(0,1fr)] gap-3.5 items-center">
                        <span className="w-11 h-11 rounded-lg grid place-items-center bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 text-slate-400">
                            <Loader2 className="w-5 h-5 animate-spin" />
                        </span>
                        <div className="min-w-0">
                            <p className="font-medium text-slate-900 dark:text-white truncate">{recording!.FileName}</p>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Uploaded. Preparing it for streaming, which usually takes a few minutes.</p>
                        </div>
                    </div>
                    <p className={NOTE}>
                        <Info className="w-3.5 h-3.5 mt-0.5 flex-none text-slate-400" />
                        <span>You can leave this page. The sign-off list updates when it’s ready.</span>
                    </p>
                </>
            )
        }
        if (view === 'failed' || stuck) {
            // A stuck recording is not failed, but the API takes Retry for it, and a new upload replaces it, the same way.
            const failed = view === 'failed'
            // The server's sign-off item says only that it failed; the recording says why, when the pipeline knew.
            const problem = stuck ? PREPARING_TOO_LONG : item ? recording!.FailureMessage?.trim() || item.Message : null
            return (
                <>
                    <div className="grid grid-cols-[44px_minmax(0,1fr)] gap-3.5 items-center">
                        <span className={`w-11 h-11 rounded-lg grid place-items-center border ${failed
                            ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                            : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 text-slate-400'}`}>
                            <Film className="w-5 h-5" />
                        </span>
                        <div className="min-w-0">
                            <p className="font-medium text-slate-900 dark:text-white truncate">{recording!.FileName}</p>
                            <p className="text-sm text-slate-500 dark:text-slate-400">{failedFacts(recording!, now)}</p>
                        </div>
                    </div>
                    {problem && (
                        <div role={failed ? 'alert' : 'status'} className={failed ? STRIP_ERROR : STRIP_WARNING}>
                            <AlertTriangle className={`w-4 h-4 mt-0.5 flex-none ${failed ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
                            <div>
                                <p>{problem}</p>
                                {canEdit && (
                                    <div className="mt-2.5 flex flex-wrap gap-2">
                                        <button type="button" className={BUTTON} disabled={locked || busy} title={lockTitle} onClick={() => void retry(recording!.Id)}>
                                            <RotateCcw className="w-3.5 h-3.5" />Try again
                                        </button>
                                        <button type="button" className={BUTTON_GHOST} disabled={locked || busy} title={lockTitle} onClick={() => setDropZoneOpen(true)}>Upload a different file</button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )
        }

        const ready = recording!
        const long = view === 'toolong'
        const facts = recordingFacts(ready)
        const duration = ready.DurationSeconds != null ? formatRecordingLength(ready.DurationSeconds) : null
        const qaAfter = ready.DurationSeconds != null ? qaAfterVideoLine(ready.DurationSeconds, sessionSeconds) : null
        const uploadedBy = uploadedByLine(ready, now)
        const replaceButton = canEdit && (
            <button type="button" className={BUTTON} disabled={replaceDisabled} title={replaceTitle} onClick={openReplace}>
                <Upload className="w-3.5 h-3.5" />Replace
            </button>
        )
        const details = (
            <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-900 dark:text-white truncate">{ready.FileName}</p>
                <p className="text-sm tabular-nums text-slate-500 dark:text-slate-400">
                    {duration && facts.startsWith(duration)
                        ? <><span className={long ? 'font-semibold text-amber-700 dark:text-amber-400' : ''}>{duration}</span>{facts.slice(duration.length)}</>
                        : facts}
                </p>
                {uploadedBy && <p className="text-sm text-slate-500 dark:text-slate-400">{uploadedBy}</p>}
                {!previewOpen && canEdit && (
                    <div className="mt-3.5 flex flex-wrap gap-2">
                        {ready.HasPlayback && <button type="button" className={BUTTON} onClick={() => openPreview(false)}><Play className="w-3 h-3" />Preview</button>}
                        {replaceButton}
                    </div>
                )}
            </div>
        )
        return (
            <>
                {previewOpen ? (
                    <>
                        <RoomRecordingPreview
                            key={previewWithSubtitles ? 'with-subtitles' : 'plain'}
                            workspaceId={workspaceId}
                            webId={webId}
                            recording={ready}
                            polls={polls}
                            subtitlesOn={previewWithSubtitles}
                            onClose={() => setPreviewOpen(false)}
                        />
                        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">{details}{replaceButton}</div>
                    </>
                ) : (
                    <div className="grid gap-4">
                        <div className="relative aspect-video rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 bg-[#edf1f0] dark:bg-slate-900/40 grid place-items-center text-slate-400">
                            <Film className="w-9 h-9" aria-hidden="true" />
                            {duration && <span className="absolute right-1.5 top-1.5 px-1.5 rounded bg-[rgb(10_24_30/0.74)] text-[11px] font-semibold leading-4 tabular-nums text-[#f2f8f8]">{duration}</span>}
                        </div>
                        {details}
                    </div>
                )}
                {qaAfter && (
                    <p className={NOTE}>
                        <Info className="w-3.5 h-3.5 mt-0.5 flex-none text-slate-400" />
                        <span>{qaAfter}</span>
                    </p>
                )}
                {long && item && !replacementOnItsWay && (
                    <div role="status" className={STRIP_WARNING}>
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-none text-amber-600 dark:text-amber-400" />
                        <div>
                            <p>{item.Message}</p>
                            {canEdit && (
                                <div className="mt-2.5 flex flex-wrap gap-2">
                                    <button type="button" className={BUTTON} disabled={locked || busy} title={lockTitle} onClick={() => void fitSessionToRecording()}>
                                        <Calendar className="w-3.5 h-3.5" />Change the end time
                                    </button>
                                    <button type="button" className={BUTTON_GHOST} disabled={replaceDisabled} title={replaceTitle} onClick={openReplace}>Upload a shorter cut</button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {replacement && (
                    <ReplacementStrip
                        view={replacement}
                        pending={pending}
                        upload={upload && replacing ? upload : null}
                        copy={REPLACEMENT_COPY}
                        now={now}
                        canEdit={canEdit}
                        locked={locked}
                        lockTitle={lockTitle}
                        busy={busy}
                        onCancel={cancelOnItsWay}
                        onRetry={retry}
                        onUploadDifferent={() => setDropZoneOpen(true)}
                    />
                )}
                {dropZoneOpen && !replacement && (
                    <div className="mt-4">
                        {chooser(true)}
                        <div className="mt-2.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 text-sm text-slate-500 dark:text-slate-400">
                            <span>
                                The current recording stays in use until the new one is ready. Polls stay as they are
                                {ready.Subtitles ? '; its subtitles are removed with it, as they’re timed to this recording.' : '.'}
                            </span>
                            <button type="button" className={BUTTON_GHOST} onClick={closeChoosers}>Keep this recording</button>
                        </div>
                    </div>
                )}
            </>
        )
    })()

    return (
        <section className={CARD} aria-labelledby="room-recording-title">
            <div className="flex items-center justify-between gap-3">
                <h2 id="room-recording-title" className={CARD_TITLE}><Film className="w-5 h-5 text-primary-600" /> Recording</h2>
                {pill}
            </div>
            <p className={CARD_SUB}>{recordingSubLine(event, recording?.State === 'Ready', ended)}</p>
            <div className="mt-5">{body}</div>
            <RoomSubtitlesSection
                web={web}
                recording={recording}
                isSignedOff={isSignedOff}
                previewOpen={previewOpen}
                onPreview={() => openPreview(true)}
                reload={reload}
            />
        </section>
    )
}
